import { userService } from '@/services'

interface User {
  id: string
  email: string
}

interface BaseNode {
  id: string
  name: string
  creator?: { id: string }
  users?: User[]
  workspace?: { users?: User[] }
}

/**
 * Factory function to create permission checkers for different node types
 */
export function createPermissionChecker(nodeType: string) {
  return async (node: BaseNode, currentUser: User): Promise<boolean> => {
    if (!node || !currentUser) return false

    switch (nodeType) {
      case 'workspace':
        // Only workspace creator can invite
        return node.creator?.id === currentUser.id

      case 'project':
        // Project creator or workspace members can invite
        if (node.creator?.id === currentUser.id) return true
        return node.workspace?.users?.some((u: User) => u.id === currentUser.id) || false

      default:
        // Default: only creator can invite
        return node.creator?.id === currentUser.id
    }
  }
}

/**
 * Factory function to create user existence checkers for different node types
 */
export function createUserExistsChecker(_nodeType: string) {
  return async (_node: BaseNode, email: string): Promise<boolean> => {
    // Just check if user exists in the system, regardless of node permissions
    const users = await userService.getAll()
    const existingUser = users.find((u: User) => u.email.toLowerCase() === email.toLowerCase())
    return !!existingUser
  }
}

/**
 * Factory function to create member checkers for different node types
 */
export function createMemberChecker(nodeType: string) {
  return async (node: BaseNode, email: string): Promise<boolean> => {
    if (!node) return false

    // Check if user is already a member by email (since they might not exist in userService yet)
    const isMemberByEmail =
      node.users?.some((u: User) => u.email.toLowerCase() === email.toLowerCase()) || false

    if (isMemberByEmail) return true

    // Also check by user ID if the user exists in the system
    const users = await userService.getAll()
    const existingUser = users.find((u: User) => u.email.toLowerCase() === email.toLowerCase())

    if (existingUser) {
      switch (nodeType) {
        case 'workspace':
        case 'project':
          return node.users?.some((u: User) => u.id === existingUser.id) || false

        default:
          return false
      }
    }

    return false
  }
}

/**
 * Factory function to create member addition functions for different node types
 */
export function createAddMemberFunction(
  nodeType: string,
  service: { addUser: (id: string, userId: string) => Promise<unknown> }
) {
  return async (node: BaseNode, email: string, _role: string): Promise<void> => {
    if (!node) throw new Error(`${nodeType} not found`)

    const users = await userService.getAll()
    const existingUser = users.find((u: User) => u.email.toLowerCase() === email.toLowerCase())

    if (existingUser) {
      // User exists, add them normally
      if (nodeType === 'workspace') {
        await service.addUser(node.id, existingUser.id)
      } else if (nodeType === 'project') {
        await service.addUser(node.id, existingUser.id)
      } else {
        throw new Error(`Unsupported node type: ${nodeType}`)
      }
    } else {
      // User doesn't exist - for prototype, just log the invitation
      // In a real app, this would send an invitation email
      console.log(`Invitation sent to ${email} for ${nodeType} ${node.name}`)
      // Don't actually add to the node since user doesn't exist yet
      // In a real app, this would create a pending invitation
    }
  }
}

/**
 * Factory function to create role permissions components for different node types
 */
export function createRolePermissionsComponent(nodeType: string) {
  return () => getRolePermissionsContent(nodeType)
}

/**
 * Helper function that returns role permissions JSX content for different node types
 */
export function getRolePermissionsContent(type: string) {
  switch (type) {
    case 'workspace':
      return (
        <>
          <h4 className="font-medium">Role Permissions:</h4>
          <ul className="space-y-1 text-muted-foreground">
            <li>
              <strong>SuperAdmin:</strong> Full access to all workspace features including deletion
            </li>
            <li>
              <strong>Admin:</strong> Can manage workspace settings and members
            </li>
            <li>
              <strong>Member:</strong> Can view and contribute to workspace content
            </li>
          </ul>
        </>
      )
    case 'project':
      return (
        <>
          <h4 className="font-medium">Project Role Permissions:</h4>
          <ul className="space-y-1 text-muted-foreground">
            <li>
              <strong>SuperAdmin:</strong> Full access to all project features including deletion
            </li>
            <li>
              <strong>Admin:</strong> Can manage project settings and members
            </li>
            <li>
              <strong>Member:</strong> Can view and contribute to project content
            </li>
          </ul>
        </>
      )
    default:
      return (
        <>
          <h4 className="font-medium">Role Permissions:</h4>
          <ul className="space-y-1 text-muted-foreground">
            <li>
              <strong>SuperAdmin:</strong> Full access to all features including deletion
            </li>
            <li>
              <strong>Admin:</strong> Can manage settings and members
            </li>
            <li>
              <strong>Member:</strong> Can view and contribute to content
            </li>
          </ul>
        </>
      )
  }
}
